function getTaxConfig() {
    var _a, _b;
    const win = window;
    return (_b = (_a = win === null || win === void 0 ? void 0 : win.theme) === null || _a === void 0 ? void 0 : _a.priceDisplayTax) !== null && _b !== void 0 ? _b : { enabled: false, rate: 10, exemptTagsRaw: '' };
}
function isExempt(cfg, tags) {
    const exemptTags = (cfg.exemptTagsRaw || '')
        .split(',')
        .map(t => t.trim().toLowerCase())
        .filter(Boolean);
    if (exemptTags.length === 0 || !Array.isArray(tags))
        return false;
    const lowerTags = tags.map(t => t.toLowerCase().trim());
    return exemptTags.some(et => lowerTags.includes(et));
}
/**
 * Returns the display price (in dollars) with tax applied if configured.
 * Works in cents internally to match Liquid's integer rounding behaviour,
 * so results stay in sync with price.liquid / product-price.liquid.
 */
export function getDisplayPriceValue(priceInDollars, tags) {
    const cfg = getTaxConfig();
    if (!cfg.enabled || !(priceInDollars > 0))
        return priceInDollars;
    if (isExempt(cfg, tags))
        return priceInDollars;
    const rate = parseFloat(String(cfg.rate)) || 0;
    const cents = Math.round(priceInDollars * 100);
    const taxedCents = Math.round(cents * (100 + rate) / 100);
    return taxedCents / 100;
}
/**
 * Formats a price (in dollars) with tax applied, returning a string for display.
 */
export function formatDisplayPrice(priceInDollars, tags) {
    return getDisplayPriceValue(priceInDollars, tags).toFixed(2);
}
/**
 * Reverse of getDisplayPriceValue: converts an incl-tax display price back to
 * the ex-tax value expected by the search provider. Used for price filter queries.
 * Returns a float (not rounded) so filter bounds are never accidentally narrowed.
 */
export function exTaxFromDisplayDollars(displayDollars) {
    const cfg = getTaxConfig();
    if (!cfg.enabled || !(displayDollars > 0))
        return displayDollars;
    const rate = parseFloat(String(cfg.rate)) || 0;
    if (rate === 0)
        return displayDollars;
    return displayDollars / (1 + rate / 100);
}
/**
 * Convert price from various formats to number
 */
export function normalizePrice(price) {
    if (typeof price === 'number') {
        return price;
    }
    if (typeof price === 'string') {
        // Remove currency symbols, spaces, and commas
        const cleaned = price.replace(/[^0-9.-]/g, '');
        return parseFloat(cleaned) || 0;
    }
    if (price && typeof price === 'object') {
        // Handle object with amount field
        if ('amount' in price) {
            return normalizePrice(price.amount);
        }
        // Handle object with value field
        if ('value' in price) {
            return normalizePrice(price.value);
        }
    }
    return 0;
}
/**
 * Convert price from cents to dollars
 */
export function centsToPrice(cents) {
    const numericCents = typeof cents === 'string' ? parseInt(cents, 10) : cents;
    return numericCents / 100;
}
/**
 * Create a Price object from various data formats
 */
export function createPrice(priceData) {
    const price = {
        regular: 0
    };
    // Simple number or string
    if (typeof priceData === 'number' || typeof priceData === 'string') {
        price.regular = normalizePrice(priceData);
        return price;
    }
    // Object with various price fields
    if (priceData && typeof priceData === 'object') {
        // Regular price
        price.regular = normalizePrice(priceData.regular ||
            priceData.price ||
            priceData.regularPrice ||
            priceData.regular_price ||
            priceData.amount ||
            priceData.value ||
            0);
        // Sale price
        const salePrice = normalizePrice(priceData.sale ||
            priceData.salePrice ||
            priceData.sale_price ||
            priceData.discounted ||
            priceData.discountedPrice ||
            0);
        if (salePrice > 0 && salePrice < price.regular) {
            price.sale = salePrice;
        }
        // Special price
        const specialPrice = normalizePrice(priceData.special ||
            priceData.specialPrice ||
            priceData.special_price ||
            0);
        if (specialPrice > 0 && specialPrice < price.regular) {
            price.special = specialPrice;
        }
        // Compare at price (use as regular if higher)
        const comparePrice = normalizePrice(priceData.compareAt ||
            priceData.compare_at ||
            priceData.compareAtPrice ||
            priceData.compare_at_price ||
            0);
        if (comparePrice > price.regular) {
            price.sale = price.regular;
            price.regular = comparePrice;
        }
    }
    return price;
}
/**
 * Get the effective price (lowest available price)
 */
export function getEffectivePrice(price) {
    var _a, _b;
    return Math.min(price.regular, (_a = price.sale) !== null && _a !== void 0 ? _a : price.regular, (_b = price.special) !== null && _b !== void 0 ? _b : price.regular);
}
/**
 * Calculate discount percentage
 */
export function calculateDiscount(price) {
    const effective = getEffectivePrice(price);
    if (effective >= price.regular) {
        return 0;
    }
    return Math.floor(((price.regular - effective) / price.regular) * 100);
}
/**
 * Format price for display
 */
export function formatPrice(price, currency = 'USD', locale = 'en-US') {
    return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(price);
}
/**
 * Check if product is on sale
 */
export function isOnSale(price) {
    return !!(price.sale || price.special) && getEffectivePrice(price) < price.regular;
}
/**
 * Create price from min/max variant prices
 */
export function createPriceFromRange(minPrice, maxPrice, compareAtPrice) {
    const min = normalizePrice(minPrice);
    const max = normalizePrice(maxPrice);
    const compareAt = normalizePrice(compareAtPrice);
    const price = {
        regular: max || min
    };
    // If min and max are different, min is the sale price
    if (min < max && min > 0) {
        price.sale = min;
    }
    // If compare at price is higher, use it as regular price
    if (compareAt > price.regular) {
        if (!price.sale) {
            price.sale = price.regular;
        }
        price.regular = compareAt;
    }
    return price;
}
